import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { resetTouchInputState, setTouchInputState, type TouchInputState } from '../game/touchInput';

type TouchControlsOverlayProps = {
  visible: boolean;
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 70,
};

const padStyle: CSSProperties = {
  position: 'absolute',
  left: 24,
  bottom: 28,
  width: 132,
  height: 132,
  borderRadius: '50%',
  border: '1px solid rgba(98,221,255,0.35)',
  background: 'radial-gradient(circle, rgba(98,221,255,0.12), rgba(7,12,20,0.18))',
  boxShadow: '0 0 26px rgba(98,221,255,0.14)',
  pointerEvents: 'auto',
  touchAction: 'none',
};

const buttonBase: CSSProperties = {
  position: 'absolute',
  width: 64,
  height: 64,
  borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.22)',
  background: 'linear-gradient(180deg, rgba(7,12,20,0.72), rgba(4,8,14,0.58))',
  color: '#edf7ff',
  fontSize: 10,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  fontFamily: 'inherit',
  pointerEvents: 'auto',
  touchAction: 'none',
};

function touchButtonStyle(right: number, bottom: number, tone: string, size = 64): CSSProperties {
  return {
    ...buttonBase,
    width: size,
    height: size,
    right,
    bottom,
    color: tone,
    fontSize: size < 64 ? 9 : 10,
    boxShadow: `0 0 22px ${tone}22`,
  };
}

export function resolveMobileButtonLayout(screenWidth: number): { jetButton: { x: number; y: number } } {
  const jetX = Math.round(screenWidth * 0.78);
  const jetY = Math.round(screenWidth * 0.15);
  return { jetButton: { x: jetX, y: jetY } };
}

type TouchButtonLayout = Record<'fire' | 'boost' | 'jump' | 'grenade' | 'pack', { right: number; bottom: number }>;

export type TouchControlLayout = {
  compactPhone: boolean;
  actionSize: number;
  movementPad: { left: number; bottom: number; width: number; height: number };
  buttonLayout: TouchButtonLayout;
};

export function resolveTouchControlLayout(width: number, height: number): TouchControlLayout {
  const compactPhone = Math.min(width, height) < 560;
  const compactLandscape = compactPhone && width > height;
  const actionSize = compactPhone ? 56 : 64;
  const movementPad = compactPhone
    ? {
        left: 22,
        bottom: compactLandscape ? 20 : 24,
        width: compactLandscape ? 108 : 116,
        height: compactLandscape ? 108 : 116,
      }
    : {
        left: 24,
        bottom: 28,
        width: 132,
        height: 132,
      };

  if (compactLandscape) {
    return {
      compactPhone,
      actionSize,
      movementPad,
      buttonLayout: {
        fire: { right: 22, bottom: 154 },
        boost: { right: 88, bottom: 154 },
        grenade: { right: 154, bottom: 154 },
        jump: { right: 88, bottom: 22 },
        pack: { right: 22, bottom: 22 },
      },
    };
  }

  return {
    compactPhone,
    actionSize,
    movementPad,
    buttonLayout: compactPhone
      ? {
          fire: { right: 24, bottom: 164 },
          boost: { right: 92, bottom: 164 },
          grenade: { right: 160, bottom: 164 },
          jump: { right: 92, bottom: 52 },
          pack: { right: 24, bottom: 52 },
        }
      : {
          fire: { right: 34, bottom: 94 },
          boost: { right: 112, bottom: 106 },
          jump: { right: 112, bottom: 34 },
          grenade: { right: 184, bottom: 100 },
          pack: { right: 250, bottom: 34 },
        },
  };
}

export function TouchControlsOverlay({ visible }: TouchControlsOverlayProps) {
  const padRef = useRef<HTMLDivElement | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 768));

  useEffect(() => {
    if (!visible) {
      resetTouchInputState();
    }
  }, [visible]);

  useEffect(() => {
    const syncViewport = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  if (!visible) return null;

  const { actionSize, movementPad, buttonLayout } = resolveTouchControlLayout(viewportWidth, viewportHeight);
  const movementPadStyle = { ...padStyle, ...movementPad };

  const setButton = (field: keyof Pick<TouchInputState, 'fire' | 'jump' | 'grenade' | 'pack' | 'boost'>, active: boolean) => {
    setTouchInputState({ [field]: active });
  };

  const updatePad = (clientX: number, clientY: number) => {
    const rect = padRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = (clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
    const dy = (clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
    setTouchInputState({ moveX: dx, moveY: -dy });
  };

  const releasePad = () => setTouchInputState({ moveX: 0, moveY: 0 });
  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    activePointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updatePad(event.clientX, event.clientY);
  };
  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current === event.pointerId) {
      updatePad(event.clientX, event.clientY);
    }
  };
  const handlePointerRelease = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current === event.pointerId) {
      activePointerIdRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      releasePad();
    }
  };

  return (
    <div aria-label="Touch controls" style={overlayStyle}>
      <div
        ref={padRef}
        role="application"
        aria-label="Touch movement pad"
        style={movementPadStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerRelease}
        onPointerCancel={handlePointerRelease}
        onMouseDown={(event) => updatePad(event.clientX, event.clientY)}
        onMouseMove={(event) => {
          if (event.buttons === 1) updatePad(event.clientX, event.clientY);
        }}
        onMouseUp={releasePad}
        onMouseLeave={releasePad}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          if (touch) updatePad(touch.clientX, touch.clientY);
        }}
        onTouchMove={(event) => {
          const touch = event.touches[0];
          if (touch) updatePad(touch.clientX, touch.clientY);
        }}
        onTouchEnd={releasePad}
      />
      <button
        type="button"
        aria-label="Touch fire"
        style={touchButtonStyle(buttonLayout.fire.right, buttonLayout.fire.bottom, '#ff8d74', actionSize)}
        onMouseDown={() => setButton('fire', true)}
        onMouseUp={() => setButton('fire', false)}
        onMouseLeave={() => setButton('fire', false)}
        onTouchStart={() => setButton('fire', true)}
        onTouchEnd={() => setButton('fire', false)}
      >
        Fire
      </button>
      <button
        type="button"
        aria-label="Touch ski boost"
        style={touchButtonStyle(buttonLayout.boost.right, buttonLayout.boost.bottom, '#d7b6ff', actionSize)}
        onMouseDown={() => setButton('boost', true)}
        onMouseUp={() => setButton('boost', false)}
        onMouseLeave={() => setButton('boost', false)}
        onTouchStart={() => setButton('boost', true)}
        onTouchEnd={() => setButton('boost', false)}
      >
        Boost
      </button>
      <button
        type="button"
        aria-label="Touch jump or jet"
        style={touchButtonStyle(buttonLayout.jump.right, buttonLayout.jump.bottom, '#62ddff', actionSize)}
        onMouseDown={() => setButton('jump', true)}
        onMouseUp={() => setButton('jump', false)}
        onMouseLeave={() => setButton('jump', false)}
        onTouchStart={() => setButton('jump', true)}
        onTouchEnd={() => setButton('jump', false)}
      >
        Jet
      </button>
      <button
        type="button"
        aria-label="Touch throw grenade"
        style={touchButtonStyle(buttonLayout.grenade.right, buttonLayout.grenade.bottom, '#ffcc66', actionSize)}
        onMouseDown={() => setButton('grenade', true)}
        onMouseUp={() => setButton('grenade', false)}
        onMouseLeave={() => setButton('grenade', false)}
        onTouchStart={() => setButton('grenade', true)}
        onTouchEnd={() => setButton('grenade', false)}
      >
        Gren
      </button>
      <button
        type="button"
        aria-label="Touch activate pack"
        style={touchButtonStyle(buttonLayout.pack.right, buttonLayout.pack.bottom, '#8ef0c9', actionSize)}
        onMouseDown={() => setButton('pack', true)}
        onMouseUp={() => setButton('pack', false)}
        onMouseLeave={() => setButton('pack', false)}
        onTouchStart={() => setButton('pack', true)}
        onTouchEnd={() => setButton('pack', false)}
      >
        Pack
      </button>
    </div>
  );
}
